/* eslint-disable import/prefer-default-export */
/* eslint-disable max-len */
/* eslint-disable camelcase */
import ppid from '../ads/gam/ppid';
import custParamsUrl from '../ads/a9/custParamsUrl';
import preBids from '../ads/prebid/bids';
import htlBids from '../ads/htlbid/bids';
import A9bids from '../ads/a9/bids';

/**
 * Get the calculated player sizing from the video iframe assuming a 16:9 ratio
 */
export const getPlayerSizing = (videoId) => {
  const iframe = document.querySelector(`[data-video_id="${videoId.toString()}"]`);
  if (! iframe) return {};
  const computedWidth = window.getComputedStyle(iframe).width;
  // eslint-disable-next-line radix
  const width = parseInt(computedWidth.replace('px', ''));
  // Calculating the video height, assuming a 16:9 ratio.
  return {
    playerwidth: width,
    playerheight: Math.round((width * 9) / 16),
  };
};

/**
 * Get the hero information if applicable.
 */
export const getHeroType = (videoId) => {
  const iframe = document.querySelector(`[data-video_id="${videoId.toString()}"]`);
  if (! iframe) return {};
  const getElementHero = (element) => {
    if ('videoPlaylist' === element.dataset?.component) {
      return element.dataset?.layout;
    }

    return (element.parentNode) ? getElementHero(element.parentNode) : false;
  };

  const hero = getElementHero(iframe);
  const validTypes = [
    'daily_news_video',
    'daily_news_main_video',
    'big_story_video',
    'big_story_video_new',
  ];

  if (! validTypes.includes(hero)) {
    return {};
  }

  return {
    hero: hero.replace(/_/g, '-'),
  };
};

/**
 * Checks if the video element with the given videoId is inside a "hero" video.
 */
export const isVideoHero = (videoId) => {
  const iframe = document.querySelector(`[data-video_id="${videoId.toString()}"]`);
  if (! iframe) return false;

  return null !== iframe.closest('.video-player--hero');
};

/**
 * Get the hero information if applicable.
 */
export const getVodOnlyParams = (videoId) => {
  const vodOnlyParams = {
    content_livestream: String(0),
  };
  const isFeed = 'feed' === videoId?.type;
  const identifier = videoId.toString();
  const isPlaylist = 1 < identifier.split(',').length;
  return (isPlaylist || isFeed || parseInt(identifier, 10)) ? vodOnlyParams : {};
};

/**
 * Prepare the custom parameters
 */
export const prepareCustParams = (videoId, isPermutiveEnabled) => {
  const persCid = String(window?.NXSTdata?.content?.persistentId);
  const iabCat = Array.isArray(window?.NXSTdata?.content?.iabCategory)
    ? window.NXSTdata.content.iabCategory[0]
    : '';
  const iabGenre = Array.isArray(window?.NXSTdata?.content?.iabGenre)
    ? window.NXSTdata.content.iabGenre[0]
    : '';
  let referralId = 'other';

  if (window.getCookie && 'function' === typeof getCookie) {
    // eslint-disable-next-line no-undef
    referralId = window.getCookie('referralId');
  }

  const custParams = {
    ...getPlayerSizing(videoId.toString()),
    ...getHeroType(videoId.toString()),
    ...getVodOnlyParams(videoId),
    // Add page uniqueId to cust_params for recording page metadata in KVPs on Akta ad calls
    upid: String(window?.NXSTdata?.page?.uniqueId ?? ''),
    refid: String(referralId),
    content_cat: iabCat ? String(iabCat) : 'undefined',
    content_genre: iabGenre ? String(iabGenre) : 'undefined',
    content_channel: window.NXSTdata?.site?.callSign?.replace(/\s/g, ''),
    station: String(window?.NXSTdata?.site?.uniqueSiteShortcode ?? ''),
    hlmeta: String(window?.NXSTdata?.page?.urlSlug ?? ''),
    ...(persCid && { pers_cid: persCid }),
  };
  if (isPermutiveEnabled) {
    custParams.permutive = encodeURIComponent(
      JSON.parse(localStorage._pdfps || '[]').slice(0, 250).join(','), // eslint-disable-line no-underscore-dangle
    );
  }
  return custParams;
};

export const isLiveStream = (videoId) => {
  const isFeed = 'feed' === videoId?.type;
  const identifier = videoId.toString();
  const isPlaylist = 1 < identifier.split(',').length;

  return (! parseInt(identifier, 10) && ! isPlaylist && ! isFeed);
};

/**
 * Update the player VAST URL with the current PPID.
 * Remove companion param value if it exists
 */
export const updateAdTagPpidAndCompanionValue = (adTagUrl, videoId) => {
  if (! ppid) {
    return adTagUrl;
  }

  let url;

  try {
    url = new URL(adTagUrl);
  } catch (e) {
    return adTagUrl;
  }

  url.searchParams.set('ppid', ppid);
  url.searchParams.set('ciu_szs', '');
  if (isLiveStream(videoId)) {
    url.searchParams.delete('ciu_szs');
  }
  // eslint-disable-next-line consistent-return
  return url;
};

/**
 * Check ad unit path and correct the value if needed
 */
const checkAdUnitPath = (adTagUrl) => {
  const adUnitPath = window?.NXSTdata?.ads?.adUnitPath;
  let url;
  if (! adUnitPath) return adTagUrl;

  try {
    url = new URL(adTagUrl);
  } catch (e) {
    return adTagUrl;
  }
  const adUnitPathToVerify = url.searchParams.get('iu');
  if (! adUnitPathToVerify) return url;
  const adUnitPathToVerifySiteId = `${adUnitPathToVerify.split('.')?.[0]}.`;
  const adUnitPathToVerifyDomain = adUnitPathToVerify.split('.')?.[1];
  if (! adUnitPathToVerifyDomain || adUnitPathToVerifyDomain.includes(adUnitPath)) return url;
  const adUnitPathToVerifyStationName = `${adUnitPathToVerifyDomain.split('/')?.[0]}/`;
  const correctAdUnitPathValue = adUnitPathToVerifySiteId + adUnitPathToVerifyStationName + adUnitPath;
  url.searchParams.set('iu', correctAdUnitPathValue);
  // eslint-disable-next-line consistent-return
  return url;
};

/**
 * Fetch bids in parallel
 * For more about Promise.all use the link below:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all
 */
export const refreshBids = (slotID) => new Promise((resolve) => {
  Promise.all([A9bids(slotID), preBids(), htlBids()]).then((bids) => resolve(bids)).catch(() => {
    const bids = [];
    // Resolve bids no matter what after two seconds(aka fetch timeout).
    setTimeout(() => {
      resolve(bids);
    }, 2000);

    // Do nothing on catch blocks,
    // Call both bids with finally block and gather/resolve the values.
    preBids().then((bid) => bids.push(bid))
      .catch(() => {})
      .finally(() => htlBids().then((bid) => bids.push(bid)))
      .catch(() => {})
      .finally(() => A9bids(slotID).then((bid) => bids.push(bid)))
      .catch(() => {})
      .finally(() => resolve(bids));
  });
});

/**
 * Add bids to the url search params.
 */
export const prepareBids = (adTagUrl, bidParams) => {
  if (! adTagUrl) return '';
  return String(custParamsUrl(bidParams, new URL(adTagUrl)));
};

// Add NLP categories and 'sent' to custom parameters.
export const addNlpValues = (custParams) => {
  const obj = { ...custParams };

  if (window && window.NXSTdata?.content?.nlpCategories) {
    // sorts depending on confidence value, category length and alphabetic, respectively
    const sortedCategories = Object.entries(
      window.NXSTdata.content.nlpCategories,
    ).sort((category1, category2) => {
      if (category1[1] !== category2[1]) {
        return category2[1] - category1[1];
      }

      if (category1[0].length !== category2[0].length) {
        return category2[0].length - category1[0].length;
      }

      return category1[0].localeCompare(category2[0]);
    });

    // Parses categories over "/" and groups w.r.t
    // https://nexstarpsg.atlassian.net/browse/WP-547
    const nlpCategories = [];

    sortedCategories.forEach((categories) => {
      categories[0].split('/').forEach((category, index) => {
        if (category) {
          let formattedCategory = category.replace(/[^A-Z0-9]+/ig, '_');
          formattedCategory = 0 === formattedCategory.indexOf('_')
            ? formattedCategory.substring(1) : formattedCategory;
          nlpCategories[index] = nlpCategories[index] || [];
          // eslint-disable-next-line max-len
          nlpCategories[index].push(formattedCategory.slice(0, 24).toLowerCase());
        }
      });
    });
    // We only care about first 3 categories
    nlpCategories.forEach((category, index) => {
      if (0 < index && 4 > index) {
        // Turn each category list from an array into a comma separated string, then encode it for use in URLs
        obj[`nlpcat${index}`] = encodeURIComponent(category.join(','));
      }
    });
  }

  if (window && window.NXSTdata?.content?.nlpSentiment) {
    const nlpScore = window.NXSTdata.content.nlpSentiment.score;
    let nlpAdScore = '0';
    if (0 !== nlpScore) {
      nlpAdScore = 0 < nlpScore ? 'positive' : 'negative';
    }
    obj.nlpsent = nlpAdScore;
  }
  // eslint-disable-next-line consistent-return
  return obj;
};

// Prepare the Ad Tag Url by adding custom params, ppid and bids
export const prepareAdTagUrl = (videoId, adTagUrl, bidParams, isPermutiveEnabled) => {
  if (! adTagUrl) return '';
  let updatedAdTagUrl;
  let custParams = prepareCustParams(videoId, isPermutiveEnabled);
  custParams = addNlpValues(custParams);
  updatedAdTagUrl = String(custParamsUrl(custParams, new URL(adTagUrl)));
  updatedAdTagUrl = updateAdTagPpidAndCompanionValue(updatedAdTagUrl, videoId);
  if (! isLiveStream) {
    updatedAdTagUrl = checkAdUnitPath(updatedAdTagUrl);
  }
  updatedAdTagUrl = prepareBids(updatedAdTagUrl, bidParams);
  return updatedAdTagUrl;
};

// Prepare custom params for freewheel params.
export const prepareFreewheelParams = (videoId, isPermutiveEnabled) => {
  let custParams = prepareCustParams(videoId, isPermutiveEnabled);
  custParams = addNlpValues(custParams);
  custParams.videoId = videoId;

  if (ppid) {
    custParams.ppid = ppid;
    custParams.ciu_szs = '';

    if (isLiveStream(videoId)) {
      delete custParams.ciu_szs;
    }
  }

  return custParams;
};

/**
 * Dispatch custom events for OVP player with name, videoId and playerId parameters.
 * These custom events are listened in video-playlist-factory class.
 * Since we are not rendering status labels in WV theme, dispatching video completed event is enough for now.
 * Other events like videoStart, videoPause and videoResume can be added once we decide to render status labels.
 */
export const customOvpEvent = (videoId, videoPayload, eventName) => window.dispatchEvent(new CustomEvent('OvpVideoEvent', {
  detail: {
    name: eventName,
    videoId,
    playerId: videoPayload.playerId,
  },
}));

/**
 * Parses the params object set in the video placeholder tag.
 */
export const parseVideoParams = (videoParams) => {
  const defaultError = new Error('Video params are not valid');
  try {
    // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.replaceAll
    const parsedParams = JSON.parse(videoParams.replaceAll('\'/', '"'));
    if (0 === Object.keys(parsedParams).length) {
      throw defaultError;
    } else {
      return parsedParams;
    }
  } catch {
    throw defaultError;
  }
};

/**
 * Returns the player position based on the params.
 */
export const getPlayerPosition = (parsedParams, isEmbedVideoInPost) => {
  const videoId = parsedParams?.video || parsedParams?.playlist.id;
  const videoType = parsedParams?.video ? 'video' : 'playlist';
  const playerElement = 'video' === videoType ? document.querySelector(`[data-video_id="${videoId}"]`)
	  : document.querySelector(`[data-video_id="feed,${videoId}"]`);
  const sectionElement = playerElement?.closest('section');
  const paramsWidgetName = parsedParams?.widgetName ?? sectionElement?.dataset?.widgetName;
  const isFromWidget = 0 < paramsWidgetName?.length;

  let widgetName;
  if (!isFromWidget) {
    // If it is empty and isEmbedVideoInPost, it means that the player is rendered from the post
    widgetName = isEmbedVideoInPost ? 'Story Embed' : 'Lead Media';
    if ('video-hero-right-rail' === sectionElement?.dataset?.moduleType) {
      widgetName = 'Home Page Video Hero Right Rail';
    }
  } else {
    widgetName = paramsWidgetName;
  }
  return widgetName;
};

/**
 * Determines the device type based on the width of the viewport.
 */
export const getDevice = () => {
  const width = (window.innerWidth || document.documentElement.clientWidth);
  let device = 'Mobile';
  if (480 <= width) {
    device = 'Tablet';
  }
  if (1024 <= width) {
    device = 'Desktop';
  }
  return device;
};

/**
 * Retrieves the video player element using the video ID.
 */
export const getPlayerElement = (videoId) => {
  if (! videoId) {
    return null;
  }
  const isFeed = 'feed' === videoId?.type;
  const identifier = videoId.toString();
  const customId = isFeed ? `${videoId?.id}` : identifier;
  return document.querySelector(`[data-video_id="${customId}"]`);
};

/**
 * Determines the playerLocation for the video based on various conditions about the video and its placement.
 */
export const getPlayerLocation = (videoId, isLeadMedia, autoPlay, isPost) => {
  const videoPlayerElement = getPlayerElement(videoId);
  const isHero = isVideoHero(videoId);

  if (isLeadMedia && isPost) return 'Lead Video';
  if (videoId && isLiveStream(videoId) && ! isHero) return 'Livestream Player';
  if (videoPlayerElement && videoPlayerElement?.closest('[data-component="videoCenter"], [data-component="videoCenterPage"]')) return 'Video Center';
  if (! autoPlay && isPost && videoPlayerElement && videoPlayerElement?.closest('.post, .article')) return 'Click to play embed';
  if (videoPlayerElement && videoPlayerElement?.closest('[id^="ns-video-bin-"]') && ! isHero) return 'Video Bin';
  if (videoId && isHero) return 'Video Hero Unit';

  return null;
};

/**
 * Applies video classification parameters to a GAM VAST ad tag URL based on the player playerLocation and platform.
 * This function updates the given ad tag URL with video classification parameters
 * such as 'placement', 'plcmt', and 'playbackmethod'
 */
export const applyVideoClassificationToAdTagUrl = (adTagUrl, params) => {
  const {
    videoClassifications, playerName, videoId, isLeadMedia, autoPlay, isPost,
  } = params;
  const playerLocation = getPlayerLocation(videoId, isLeadMedia, autoPlay, isPost);
  const platform = 'Desktop' === getDevice() ? 'Desktop' : 'Mobile/Tablet';
  // Get video classification parameters based on the player playerLocation and platform.
  const classification = videoClassifications.find((vc) => (vc.location === (playerLocation || playerName)) && vc.platform === platform);
  let url;
  try {
    url = new URL(adTagUrl) || '';
  } catch (error) {
    url = '';
  }
  if (! classification || ! url) {
    return adTagUrl;
  }
  const { plcmt } = classification;
  const searchParams = new URLSearchParams(url.search);
  // Update the URL with the video classification parameters
  if (plcmt !== undefined) searchParams.set('plcmt', plcmt); // for now only plcmt is set to adTagUrl

  url.search = searchParams.toString();
  return url.toString();
};

/**
 * Applies video classification parameters to a Freewheel VAST ad tag URL based on the player playerLocation and platform.
 * This function updates the given ad tag URL with video classification parameters
 * '_fw_placement_type', '_fw_plcmt_type'
 */
export const applyVideoClassificationToFreewheelAdTagUrl = (adTagUrl, params) => {
  const {
    videoClassifications, playerName, videoId, isLeadMedia, autoPlay, isPost,
  } = params;
  const playerLocation = getPlayerLocation(videoId, isLeadMedia, autoPlay, isPost);
  const isAmp = window.nxstVideoDeliveryIsAmp === true;
  const platform = isAmp ? 'AMP' : ('Desktop' === getDevice() ? 'Desktop' : 'Mobile/Tablet');
  // Get video classification parameters based on the player playerLocation and platform.
  const classification = videoClassifications.find((vc) => (vc.location === (playerLocation || playerName)) && vc.platform === platform);
  let url;
  try {
    url = new URL(adTagUrl) || '';
  } catch (error) {
    url = '';
  }
  if (! classification || ! url) {
    return adTagUrl;
  }
  const { plcmt, placement, playbackmethod } = classification;
  const searchParams = new URLSearchParams(url.search);
  // Update the URL with the video classification parameters
  if (plcmt !== undefined) searchParams.set('_fw_plcmt_type', plcmt);
  if (plcmt !== undefined) searchParams.set('_fw_placement_type', placement);
  if (playbackmethod !== undefined) searchParams.set('_fw_playback_method', playbackmethod);

  url.search = searchParams.toString();
  return url.toString();
};

/**
 * Check for the ima sdk variables loaded by the scrips
 * @returns {boolean} ads can be run
 */
export function canRunAds() {
  // the ima sdk scripts files are loaded sync and video delivery depends on them
  // that asures that this variables are defined when we call them
  // eslint-disable-next-line no-prototype-builtins
  return window.google?.hasOwnProperty('ima');
}

/**
 * Check for url params added to the webview by the mobile team.
 * @returns {boolean} comscore should be enabled
 */
export function shouldDisableComscoreBasedOnParams() {
  let isApp = false;
  let url;
  try {
    url = new URL(window?.parent?.location?.href) || '';
  } catch (error) {
    url = '';
  }

  if (url === '') {
    return isApp;
  }
  const searchParams = new URLSearchParams(url.search);
  // get page param from the webview passed by the mobile team
  const utmSource = searchParams.get('utm_medium');
  const utmContent = searchParams.get('utm_content');

  if (utmSource === 'mobileapp' && utmContent === 'webview' ) {
    isApp = true;
  }

  return isApp;
  }

/**
 * Utility function to get video classification
 *
 * @param {Object} video_classification - The dataset object containing video classification data.
 * @param {Object} playerParams - The player parameters object.
 * @returns {Object} - The parsed video classification.
 */
export function getVideoClassification(video_classification, playerParams) {
	return video_classification
		? parseVideoParams(video_classification)
		: playerParams;
}
